import { Autocomplete, AutocompleteOption, Chip, ListItemDecorator, createFilterOptions } from "@mui/joy";
import { capitalizeFirstLetter } from "@/utils/strings";
import { EmojiDisplay } from "@/components/EmojiDisplay";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useCallback, useMemo, forwardRef } from "react";

const baseFilter = createFilterOptions();

const normalizeTag = (tag) => tag.toLowerCase().trim();

const extractTagName = (item) => {
  if (typeof item === 'string') return item;
  if (item.inputValue) return item.inputValue;
  return item.name || '';
};

const deduplicateAndNormalize = (items) => {
  const seen = new Set();
  return items.reduce((tags, item) => {
    const normalized = normalizeTag(extractTagName(item));
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      tags.push(normalized);
    }
    return tags;
  }, []);
};

const addTagIfNew = (currentTags, newTag) => {
  const normalized = normalizeTag(newTag);
  if (!normalized) return currentTags;
  
  const exists = currentTags.some(t => normalizeTag(t) === normalized);
  if (exists) return currentTags;
  
  return [...currentTags, normalized];
};

export const TagsSelect = forwardRef(function TagsSelect({ value = [], onChange, options = [], placeholder, allowCreate = false, startDecorator, sx }, ref) {
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = useCallback((_, newInputValue, reason) => {
    if (reason !== 'input') return;

    if (!allowCreate) {
      setInputValue(newInputValue);
      return;
    }

    if (!newInputValue.includes(',')) {
      setInputValue(newInputValue);
      return;
    }

    const parts = newInputValue.split(',');
    const completeParts = parts.slice(0, -1);
    const remainder = parts[parts.length - 1].trim();

    const updatedTags = completeParts.reduce(addTagIfNew, [...value]);
    onChange(updatedTags);
    setInputValue(remainder);
  }, [allowCreate, value, onChange]);

  const handleChange = useCallback((_, newValue) => {
    if (allowCreate) {
      onChange(deduplicateAndNormalize(newValue));
    } else {
      onChange(newValue.map(tag => tag.name));
    }
    setInputValue('');
  }, [allowCreate, onChange]);

  const handleBlur = useCallback(() => {
    if (!allowCreate) return;
    
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    onChange(addTagIfNew(value, trimmed));
    setInputValue('');
  }, [allowCreate, inputValue, value, onChange]);

  const filterOptions = useCallback((opts, params) => {
    const filtered = baseFilter(opts, params);
    
    if (!allowCreate) return filtered;

    const normalizedInput = normalizeTag(params.inputValue);
    if (!normalizedInput) return filtered;

    const existsInOptions = opts.some(opt => normalizeTag(opt.name) === normalizedInput);
    const alreadySelected = value.some(t => normalizeTag(t) === normalizedInput);

    if (!existsInOptions && !alreadySelected) {
      filtered.push({
        inputValue: normalizedInput,
        name: `Create "${capitalizeFirstLetter(normalizedInput)}"`,
        isNew: true,
      });
    }

    return filtered;
  }, [allowCreate, value]);

  const getOptionLabel = (option) => {
    if (typeof option === 'string') return capitalizeFirstLetter(option);
    if (option.inputValue) return option.name;
    return capitalizeFirstLetter(option.name);
  };

  const emojiByName = useMemo(() => {
    const entries = options.map(opt => [normalizeTag(opt.name), opt.emoji]);
    return Object.fromEntries(entries);
  }, [options]);

  const renderOption = ({ key, ...optionProps }, option) => (
    <AutocompleteOption key={key ?? getOptionLabel(option)} {...optionProps}>
      <ListItemDecorator sx={{ minInlineSize: 24 }}>
        {option.emoji ? <EmojiDisplay emoji={option.emoji} size={16} /> : null}
      </ListItemDecorator>
      {getOptionLabel(option)}
    </AutocompleteOption>
  );

  const renderTags = (tagValues, getTagProps) =>
    tagValues.map((option, index) => {
      const { key, onDelete, ...tagProps } = getTagProps({ index });
      const tagName = extractTagName(option);
      const emoji = emojiByName[normalizeTag(tagName)];
      return (
        <Chip
          key={key ?? index}
          size="sm"
          variant="soft"
          color="primary"
          startDecorator={emoji ? <EmojiDisplay emoji={emoji} size={14} /> : undefined}
          endDecorator={<CloseIcon sx={{ fontSize: 14, cursor: 'pointer' }} onClick={onDelete} />}
          {...tagProps}
        >
          {capitalizeFirstLetter(tagName)}
        </Chip>
      );
    });

  const selectedTagObjects = allowCreate 
    ? value 
    : options.filter(tag => value.includes(tag.name));

  const computedPlaceholder = placeholder ?? (allowCreate ? "Add tags" : "Select tags");

  return (
    <Autocomplete
      ref={ref}
      multiple
      freeSolo={allowCreate}
      value={selectedTagObjects}
      inputValue={inputValue}
      options={options}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onBlur={handleBlur}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, val) => {
        const optName = typeof option === 'string' ? option : option.name;
        const valName = typeof val === 'string' ? val : val.name;
        return normalizeTag(optName) === normalizeTag(valName);
      }}
      renderOption={renderOption}
      renderTags={renderTags}
      placeholder={selectedTagObjects.length === 0 ? computedPlaceholder : ""}
      startDecorator={startDecorator}
      sx={sx}
    />
  );
});

